import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Download, Minus, Plus, X } from "lucide-react";
import { useFocusTrap } from "../../hooks/use-focus-trap";
import LoadingLabel from "../../components/LoadingLabel";

// A full-screen photo you can pinch, drag and double-tap, for reading a receipt on a phone.
// The image is shown fitted to the screen; the transform is x/y (pixels from the centre) and a scale.

type Transform = { s: number; x: number; y: number };
type Point = { x: number; y: number };
const MAX_SCALE = 6;
const DOUBLE_TAP_SCALE = 2.5;
const FIT: Transform = { s: 1, x: 0, y: 0 };
const url = (id: string, extra = "") => `/api/receipts/image?id=${encodeURIComponent(id)}&v=2${extra}`;

export default function PhotoViewer({ images, start = 0, title, filename, onClose }: {
  images: string[]; start?: number; title: string; filename: string; onClose: () => void;
}) {
  const [index, setIndex] = useState(Math.min(Math.max(start, 0), images.length - 1));
  const [view, setViewState] = useState<Transform>(FIT);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [gesturing, setGesturing] = useState(false);
  const root = useRef<HTMLDivElement>(null), stage = useRef<HTMLDivElement>(null), img = useRef<HTMLImageElement>(null);
  const current = useRef<Transform>(FIT);
  const pointers = useRef(new Map<number, Point>());
  const gesture = useRef({ base: FIT, dist: 0, mid: { x: 0, y: 0 }, origin: { x: 0, y: 0 }, moved: false, pinched: false });
  const lastTap = useRef({ at: 0, x: 0, y: 0 });
  useFocusTrap(root, () => undefined);

  const apply = useCallback((next: Transform) => {
    const area = stage.current, photo = img.current;
    const s = Math.min(MAX_SCALE, Math.max(1, next.s));
    // Keep the photo covering the screen once it is bigger than it; centred while it is smaller.
    const limitX = area && photo ? Math.max(0, (photo.offsetWidth * s - area.clientWidth) / 2) : 0;
    const limitY = area && photo ? Math.max(0, (photo.offsetHeight * s - area.clientHeight) / 2) : 0;
    const value = { s, x: Math.min(limitX, Math.max(-limitX, next.x)), y: Math.min(limitY, Math.max(-limitY, next.y)) };
    current.current = value;
    setViewState(value);
  }, []);
  // Positions are measured from the centre of the screen, which is the photo's own centre at rest.
  const local = (e: { clientX: number; clientY: number }): Point => {
    const rect = stage.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left - rect.width / 2, y: e.clientY - rect.top - rect.height / 2 };
  };
  // Zooms so the point under `at` stays under it.
  const zoomAt = (scale: number, at: Point, base: Transform = current.current) => {
    const s = Math.min(MAX_SCALE, Math.max(1, scale));
    const cx = (at.x - base.x) / base.s, cy = (at.y - base.y) / base.s;
    apply({ s, x: at.x - cx * s, y: at.y - cy * s });
  };
  const go = useCallback((to: number) => {
    setIndex(to); setLoaded(false); setLoadError(false); apply(FIT);
  }, [apply]);

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); e.preventDefault(); onClose(); }
      else if (e.key === "ArrowLeft" && index > 0) go(index - 1);
      else if (e.key === "ArrowRight" && index < images.length - 1) go(index + 1);
      else if (e.key === "+" || e.key === "=") zoomAt(current.current.s * 1.5, { x: 0, y: 0 });
      else if (e.key === "-") zoomAt(current.current.s / 1.5, { x: 0, y: 0 });
    };
    // Capture, so closing the viewer does not also close the transaction it was opened from.
    window.addEventListener("keydown", key, true);
    return () => window.removeEventListener("keydown", key, true);
  });
  useEffect(() => {
    const area = stage.current;
    if (!area) return;
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomAt(current.current.s * Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.002)), local(e));
    };
    area.addEventListener("wheel", wheel, { passive: false });
    return () => area.removeEventListener("wheel", wheel);
  });

  const down = (e: React.PointerEvent) => {
    stage.current!.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, local(e));
    const g = gesture.current;
    setGesturing(true);
    if (pointers.current.size === 1) { g.base = current.current; g.origin = local(e); g.moved = false; g.pinched = false; }
    else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      g.base = current.current; g.dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      g.mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; g.pinched = true;
    }
  };
  const move = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    const at = local(e), g = gesture.current;
    pointers.current.set(e.pointerId, at);
    if (pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y), mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const s = Math.min(MAX_SCALE, Math.max(1, g.base.s * (dist / g.dist)));
      const cx = (g.mid.x - g.base.x) / g.base.s, cy = (g.mid.y - g.base.y) / g.base.s;
      apply({ s, x: mid.x - cx * s, y: mid.y - cy * s });
    } else {
      if (Math.hypot(at.x - g.origin.x, at.y - g.origin.y) > 8) g.moved = true;
      if (g.base.s > 1) apply({ s: g.base.s, x: g.base.x + at.x - g.origin.x, y: g.base.y + at.y - g.origin.y });
    }
  };
  const up = (e: React.PointerEvent) => {
    if (!pointers.current.delete(e.pointerId)) return;
    const g = gesture.current;
    if (pointers.current.size === 1) {
      // One finger lifted from a pinch: carry on dragging with the other, from where it is now.
      g.base = current.current; g.origin = [...pointers.current.values()][0];
      return;
    }
    if (pointers.current.size > 0) return;
    setGesturing(false);
    if (g.moved || g.pinched) return;
    const at = local(e), now = Date.now(), tap = lastTap.current;
    if (now - tap.at < 320 && Math.hypot(at.x - tap.x, at.y - tap.y) < 30) {
      if (current.current.s > 1) apply(FIT); else zoomAt(DOUBLE_TAP_SCALE, at);
      lastTap.current = { at: 0, x: 0, y: 0 };
    } else lastTap.current = { at: now, x: at.x, y: at.y };
  };

  const label = images.length > 1 ? `${title} (photo ${index + 1} of ${images.length})` : title;
  const download = url(images[index], `&download=1&name=${encodeURIComponent(filename + (images.length > 1 ? ` ${index + 1}` : ""))}`);
  return createPortal(
    <div ref={root} className="photo-viewer" role="dialog" aria-modal="true" aria-label={label} tabIndex={-1}>
      <div className="photo-viewer-bar">
        <strong>{title}</strong>
        <a className="photo-viewer-button" href={download} download aria-label="Download photo" title="Download"><Download size={20} /></a>
        <button className="photo-viewer-button" onClick={onClose} aria-label="Close photo"><X size={22} /></button>
      </div>
      <div
        ref={stage}
        className="photo-viewer-stage"
        style={!loaded ? {
          backgroundImage: `url("${url(images[index], "&size=thumb")}")`,
          backgroundSize: "contain",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        } : undefined}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
      >
        <img
          ref={img}
          key={images[index]}
          src={url(images[index])}
          alt={label}
          draggable={false}
          onLoad={() => setLoaded(true)}
          onError={() => setLoadError(true)}
          style={{
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.s})`,
            transition: gesturing ? "none" : "transform 0.18s ease-out",
            opacity: loaded ? 1 : 0,
          }}
        />
        {!loaded && !loadError && <p className="photo-viewer-loading"><LoadingLabel>Loading photo</LoadingLabel></p>}
        {loadError && !loaded && <p className="photo-viewer-loading">Preview photo shown</p>}
      </div>
      <div className="photo-viewer-bar photo-viewer-controls">
        {images.length > 1 && <button className="photo-viewer-button" onClick={() => go(index - 1)} disabled={index === 0} aria-label="Previous photo"><ChevronLeft size={22} /></button>}
        <button className="photo-viewer-button" onClick={() => zoomAt(view.s / 1.5, { x: 0, y: 0 })} disabled={view.s <= 1} aria-label="Zoom out"><Minus size={20} /></button>
        <span className="photo-viewer-hint">{images.length > 1 ? `${index + 1} / ${images.length}` : "Pinch or double-tap to zoom"}</span>
        <button className="photo-viewer-button" onClick={() => zoomAt(view.s * 1.5, { x: 0, y: 0 })} disabled={view.s >= MAX_SCALE} aria-label="Zoom in"><Plus size={20} /></button>
        {images.length > 1 && <button className="photo-viewer-button" onClick={() => go(index + 1)} disabled={index === images.length - 1} aria-label="Next photo"><ChevronRight size={22} /></button>}
      </div>
    </div>,
    document.body,
  );
}
